import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Play, Pause, CheckCircle2, Sparkles, Phone, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { useBranding, withAlpha } from "../../lib/branding";

const STYLE_TINT = {
  Professional: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  Warm: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  Energetic: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  Calm: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

export default function PortalAgent() {
  const branding = useBranding();
  const accent = branding?.accent_color || "#FFB000";

  const [personas, setPersonas] = useState([]);
  const [current, setCurrent] = useState(null);  // {persona, custom_agent_name, agent_name}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [customName, setCustomName] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: a }] = await Promise.all([
        api.get("/portal/personas"),
        api.get("/portal/agent"),
      ]);
      setPersonas(p.items || []);
      setCurrent(a);
      setSelectedId(a.persona?.id || "");
      setCustomName(a.custom_agent_name || "");
    } catch (err) {
      toast.error("Could not load agent settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const playSample = async (persona) => {
    if (!persona.sample_url) {
      toast.message("Voice sample is being prepared — try again in a moment");
      return;
    }
    if (playingId === persona.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    try {
      // Authenticated fetch of the MP3 (browser <audio> can't pass our bearer token)
      const token = localStorage.getItem("ra_token");
      const url = `${API}${persona.sample_url}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("not ok");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audio.onended = () => setPlayingId(null);
      audioRef.current = audio;
      setPlayingId(persona.id);
      audio.play();
    } catch {
      toast.error("Couldn't play sample");
      setPlayingId(null);
    }
  };

  const save = async () => {
    if (!selectedId) {
      toast.error("Pick a voice first");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/portal/agent", {
        persona_id: selectedId,
        custom_agent_name: customName.trim() || null,
      });
      setCurrent(data);
      toast.success(`Saved — meet ${data.agent_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white/55 py-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  const dirty = selectedId !== (current?.persona?.id || "") || (customName.trim() || null) !== (current?.custom_agent_name || null);

  return (
    <div className="space-y-6" data-testid="portal-agent-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: accent }}>Your AI</div>
        <h1 className="text-3xl font-bold tracking-tight">Pick the voice and name your AI</h1>
        <p className="text-sm text-white/55 mt-2 max-w-2xl">This is what your callers hear. Choose a voice style, give your AI a name that fits your business — change anytime.</p>
      </div>

      {/* Currently configured agent */}
      {current?.persona && (
        <div
          className="rounded-2xl p-6 border flex items-center gap-5"
          style={{
            background: `linear-gradient(135deg, ${withAlpha(accent, 0.08)}, transparent)`,
            borderColor: withAlpha(accent, 0.3),
          }}
          data-testid="portal-current-agent"
        >
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center border flex-shrink-0"
            style={{ background: withAlpha(accent, 0.15), borderColor: withAlpha(accent, 0.4) }}
          >
            <Mic className="w-7 h-7" style={{ color: accent }} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.15em] font-medium flex items-center gap-1" style={{ color: accent }}>
                <Sparkles className="w-3 h-3" /> Currently active
              </span>
            </div>
            <div className="text-2xl font-bold mt-1.5" data-testid="portal-current-agent-name">{current.agent_name}</div>
            <div className="text-sm text-white/55 mt-1">{current.persona.description} · {current.persona.gender} · {current.persona.style}</div>
          </div>
        </div>
      )}

      {/* Custom name */}
      <div className="bg-[#121214] border border-white/5 rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-3" style={{ color: accent }}>Agent name</div>
        <label className="block">
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={selectedId ? `Defaults to "${personas.find((p) => p.id === selectedId)?.suggested_name}"` : "Pick a voice below first"}
            maxLength={60}
            data-testid="portal-agent-name-input"
            className="w-full bg-black/30 border border-white/10 rounded-lg text-white text-base px-4 py-3 placeholder:text-white/30 focus:outline-none transition-colors"
            onFocus={(e) => { e.target.style.borderColor = accent; }}
            onBlur={(e) => { e.target.style.borderColor = ""; }}
          />
        </label>
        <div className="text-[11px] text-white/40 mt-2">Use the name your callers should hear (e.g. "Sarah", "Lisa", "Mike"). Leave blank to use the suggested name.</div>
      </div>

      {/* Test call my AI — killer demo CTA */}
      <TestCallBlock accent={accent} disabled={!current?.persona} agentName={current?.agent_name} />

      {/* Persona grid */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-3" style={{ color: accent }}>Choose a voice style</div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="portal-persona-grid">
          {personas.map((p) => {
            const isSelected = selectedId === p.id;
            const isPlaying = playingId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedId(p.id)}
                data-testid={`portal-persona-${p.suggested_name.toLowerCase()}`}
                className="text-left bg-[#121214] rounded-2xl p-5 border-2 transition-all hover:border-white/15"
                style={{
                  borderColor: isSelected ? accent : "rgba(255,255,255,0.05)",
                  boxShadow: isSelected ? `0 0 20px ${withAlpha(accent, 0.15)}` : "none",
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-base font-semibold text-white truncate">{p.suggested_name}</span>
                      {isSelected && <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border font-medium ${STYLE_TINT[p.style] || "bg-white/5 text-white/60 border-white/10"}`}>
                        {p.style}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.1em] text-white/40 capitalize">{p.gender}</span>
                    </div>
                  </div>
                  {p.sample_url && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); playSample(p); }}
                      data-testid={`portal-play-${p.suggested_name.toLowerCase()}`}
                      title="Hear a sample"
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border transition-colors"
                      style={{
                        background: isPlaying ? accent : "transparent",
                        borderColor: isPlaying ? accent : withAlpha(accent, 0.3),
                        color: isPlaying ? "#000" : accent,
                      }}
                    >
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                  )}
                </div>
                <p className="text-[13px] text-white/65 leading-relaxed">{p.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Save bar */}
      <div className="sticky bottom-4 z-20 flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || saving || !selectedId}
          data-testid="portal-save-agent-btn"
          className="font-semibold text-sm px-6 py-3 rounded-xl text-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
          style={{
            background: accent,
            boxShadow: `0 8px 24px ${withAlpha(accent, 0.3)}`,
          }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {saving ? "Saving…" : (current?.persona ? "Update my AI" : "Save my AI")}
        </button>
      </div>
    </div>
  );
}

function TestCallBlock({ accent, disabled, agentName }) {
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastCall, setLastCall] = useState(null);

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter your full 10-digit cell number");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/agent/test-call", { phone });
      setLastCall(data);
      toast.success(data.message);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(detail || (status === 429 ? "Hit rate limit — try again later" : "Couldn't place test call"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-6 border relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${withAlpha(accent, 0.1)}, ${withAlpha(accent, 0.02)})`,
        borderColor: withAlpha(accent, 0.3),
      }}
      data-testid="portal-test-call-block"
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl pointer-events-none" style={{ background: withAlpha(accent, 0.18) }} />
      <div className="relative">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center border flex-shrink-0"
            style={{ background: withAlpha(accent, 0.15), borderColor: withAlpha(accent, 0.4) }}
          >
            <PhoneCall className="w-5 h-5" style={{ color: accent }} strokeWidth={1.75} />
          </div>
          <div>
            <h3 className="text-lg font-semibold tracking-tight">Hear it for yourself</h3>
            <p className="text-sm text-white/55 mt-0.5">
              {agentName
                ? <>Enter your phone — <strong className="text-white/85">{agentName}</strong> will call you in a few seconds.</>
                : "Save your voice + name first, then test-call your AI to your phone."}
            </p>
          </div>
        </div>

        {lastCall ? (
          <div
            className="rounded-xl p-4 border flex items-start gap-3"
            style={{ background: withAlpha(accent, 0.08), borderColor: withAlpha(accent, 0.3) }}
            data-testid="portal-test-call-success"
          >
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: accent }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">Calling {lastCall.phone} now…</div>
              <div className="text-[12.5px] text-white/55 mt-1">
                Pick up to hear <strong className="text-white/85">{lastCall.agent_name}</strong>. After the call ends, the transcript appears in your <a href="../calls" style={{ color: accent }} className="hover:underline">Calls</a> log.
                {lastCall.remaining_today != null && lastCall.remaining_today > 0 && (
                  <span className="text-white/40"> · {lastCall.remaining_today} test call{lastCall.remaining_today === 1 ? "" : "s"} left today.</span>
                )}
              </div>
              <button
                onClick={() => setLastCall(null)}
                className="text-[12px] mt-3 hover:underline"
                style={{ color: accent }}
                data-testid="portal-test-call-reset"
              >
                Test another number →
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-stretch" data-testid="portal-test-call-form">
            <div className="flex-1 relative">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35 pointer-events-none" />
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
                disabled={disabled || submitting}
                data-testid="portal-test-call-phone-input"
                className="w-full bg-black/30 border border-white/10 rounded-lg text-white text-base pl-10 pr-4 py-3 placeholder:text-white/25 focus:outline-none transition-colors disabled:opacity-40"
                onFocus={(e) => { e.target.style.borderColor = accent; }}
                onBlur={(e) => { e.target.style.borderColor = ""; }}
              />
            </div>
            <button
              type="submit"
              disabled={disabled || submitting || phone.replace(/\D/g, "").length < 10}
              data-testid="portal-test-call-submit-btn"
              className="font-semibold text-sm px-6 py-3 rounded-lg text-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all whitespace-nowrap"
              style={{
                background: accent,
                boxShadow: `0 4px 16px ${withAlpha(accent, 0.25)}`,
              }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
              {submitting ? "Calling…" : "Call me now"}
            </button>
          </form>
        )}

        <div className="text-[11px] text-white/35 mt-3 leading-relaxed">
          Limit: 3 test calls per hour, 5 per day. Standard call rates apply.
        </div>
      </div>
    </div>
  );
}

